import isEmpty from 'lodash.isempty'

import { getDefaultLocaleFromCountry } from '@core/helpers/countries'
import { formatDateToYYYYMMDD } from '@core/helpers/date'
import { ALL_CATEGORIES } from '@merchant/pages/international-onboarding/InternationalOnboarding.constant'

export const mapToAddress = (address) => {
  const trim = (value) => (value || '').trim().replace(/\s/g, ' ')

  return {
    first_name: trim(address.firstName) || null,
    last_name: trim(address.lastName) || null,
    company: trim(address.company) || null,
    street: trim(address.street) || null,
    street2: trim(address.street2) || null,
    city: trim(address.city) || null,
    state_or_province: trim(address.stateOrProvince) || null,
    postal_code: trim(address.postalCode) || null,
    country: trim(address.country) || null,
    country_dial_in_code: trim(address.countryDialInCode) || null,
    phone: trim(address.phone) || null,
    gender: parseInt(String(address.gender || 0), 10),
    birthdate: formatDateToYYYYMMDD(trim(address.birthdate)) || null,
    nationality: trim(address.nationality) || null,
    customer_id_number: trim(address.customerIdNumber) || null,
  }
}

export const mapToClientAddress = ({ isShipping, isBilling, ...address }) => ({
  ...(isShipping
    ? { is_shipping: true, same_as_shipping: !!isBilling }
    : { is_shipping: false }),
  ...mapToAddress(address),
})

export const mapToNewsletterPreferences = (preferences) =>
  preferences.map((data) => ({
    email_preference: data.userPreferenceId,
    marketing_newsletter: data.newsletterId,
    optin: data.optin,
    frequency: data.frequency,
  }))

export const mapToKybProfile = (data) => ({
  pseudo: data.legalName,
  company: data.displayName,
  address__phone: data.phone,
  address__street: data.address,
  address__city: data.city,
  address__postal_code: data.zipCode,
  address__state_or_province: data.state,
  address__country: data.country,
  vat_number: data.ein,
  business_mail: data.email,
})

export const mapToKybBank = (data) => ({
  void_check: data.statement,
  merchant__wallet__account_number: data.accountNumber,
  merchant__wallet__branch_code: data.routingNumber,
  owner_name: data.owner,
  currency: data.currencyCode,
  country_code: data.countryCode,
})

export const mapToKybOwner = (data) => ({
  address__first_name: data.firstName,
  address__last_name: data.lastName,
  address__birthdate: data.birthdate,
  address__street: data.address,
  address__city: data.city,
  address__postal_code: data.zipCode,
  address__gender: parseInt(data.gender, 10),
  address__state_or_province: data.state,
  address__country: data.country,
  address__email: data.email,
  merchant__social_security_number: data.ssn,
})

export const mapToInternationalOnboarding = (payload) => {
  const finalPayload = { ...payload }

  if (!isEmpty(payload.markets)) {
    finalPayload.markets = payload.markets.map(getDefaultLocaleFromCountry)
  }

  if (!isEmpty(payload.shipping)) {
    const shippingMethods = payload.shipping.map(
      ({ countryCode, ...method }) => ({
        ...method,
        // ! for the backend, a countryCode is a locale
        countryCode: getDefaultLocaleFromCountry(countryCode),
      }),
    )

    const categoryShipping = shippingMethods.filter(
      ({ category }) => category !== ALL_CATEGORIES,
    )

    const globalShipping = shippingMethods.filter(
      ({ category }) => category === ALL_CATEGORIES,
    )

    finalPayload.shipping = { globalShipping, categoryShipping }
  }

  if (!isEmpty(payload.customerCare)) {
    finalPayload.customerCare = payload.customerCare.map(
      ({ market, ...rest }) => ({
        ...rest,
        market: getDefaultLocaleFromCountry(market),
      }),
    )
  }

  if (!isEmpty(payload.payment)) {
    finalPayload.payment = payload.payment.map(({ market, ...rest }) => ({
      ...rest,
      market: getDefaultLocaleFromCountry(market),
    }))
  }

  if (!isEmpty(payload.pricingRules) && !isEmpty(payload.pricingRules.rules)) {
    finalPayload.pricingRules = {
      rules: payload.pricingRules.rules.map((rule) => ({
        ...rule,
        targetMarket: getDefaultLocaleFromCountry(rule.targetMarket),
      })),
    }
  }

  return finalPayload
}

export const mapToUpdateListing = (payload) => {
  if (payload.markets) {
    return {
      ...payload,
      markets: Object.keys(payload.markets).reduce(
        (acc, countryCode) => ({
          ...acc,
          [getDefaultLocaleFromCountry(countryCode)]:
            payload.markets[countryCode],
        }),
        {},
      ),
    }
  }

  return payload
}

export const mapToPricingRule = (payload) => ({
  ...payload,
  targetMarket: getDefaultLocaleFromCountry(payload.targetMarket),
})

export const mapToDiscount = (payload) => ({
  ...payload,
  country_code: getDefaultLocaleFromCountry(payload.country_code),
})

export const mapToOperationSubscribe = (payload) => {
  return {
    ...payload,
    // TODO: On the endpoint, use the header instead?
    country: getDefaultLocaleFromCountry(payload.country),
  }
}

export const mapToChangeReferenceMarket = (payload) => ({
  referenceMarket: getDefaultLocaleFromCountry(payload.referenceMarket),
})

export const mapToUpdateMerchantPreferences = (payload) => ({
  markets: Object.keys(payload.markets).reduce(
    (acc, countryCode) => ({
      ...acc,
      [getDefaultLocaleFromCountry(countryCode)]: payload.markets[countryCode],
    }),
    {},
  ),
})

export const mapToUpdatePayPalActiveMarkets = (payload) => ({
  markets: payload.markets.map(getDefaultLocaleFromCountry),
})

export const mapToCreateMerchantShippingMethod = (payload) => ({
  ...payload,
  market: getDefaultLocaleFromCountry(payload.market),
})

export const mapToAddressRule = (payload) => ({
  ...payload,
  ...(isEmpty(payload.markets)
    ? {}
    : { markets: payload.markets.map(getDefaultLocaleFromCountry) }),
})

export const mapToCsvImportRequest = (data) => {
  const lang = data.get('lang')

  if (lang !== 'all') {
    data.set('lang', getDefaultLocaleFromCountry(lang))
  }

  return data
}

export const mapToCsvUpdateRequest = (payload) => {
  return {
    ...payload,
    lang:
      payload.lang === 'all'
        ? 'all'
        : getDefaultLocaleFromCountry(payload.lang),
  }
}
